import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";

export default function CustomerAuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next");
  const safeNext = nextPath && nextPath.startsWith("/") && !nextPath.startsWith("//") ? nextPath : "/account";

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/customer/login" : "/customer/register";
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      await api.post(path, body);
      nav(safeNext);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full bg-black border border-zinc-800 p-3 text-sm text-zinc-200 focus:border-brand-red outline-none";

  return (
    <div className="bg-background min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
      <Toaster theme="dark" position="top-center" />
      <div className="w-full max-w-md border border-zinc-900 bg-zinc-950 p-8">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-red">Customer account</p>
        <h1 className="mt-3 font-display uppercase text-3xl tracking-tight text-white">
          {mode === "login" ? "Welcome back" : "Create your account"}
        </h1>
        <p className="mt-2 text-sm text-zinc-400">Track your bookings, see work done on your car and never miss a service.</p>

        <button
          data-testid="customer-google-btn"
          onClick={googleLogin}
          className="mt-6 w-full inline-flex items-center justify-center gap-3 px-4 py-3 border border-zinc-700 text-white text-sm hover:border-brand-red transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C41.4 34.9 44 30 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3 text-zinc-600 text-xs font-mono uppercase tracking-widest">
          <span className="flex-1 h-px bg-zinc-800" /> or <span className="flex-1 h-px bg-zinc-800" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input data-testid="customer-name" required minLength={2} placeholder="Your name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
          )}
          <input data-testid="customer-email" required type="email" placeholder="you@example.com" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
          <input data-testid="customer-password" required type="password" minLength={8}
            placeholder={mode === "register" ? "Password (8+ characters)" : "Password"} value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} />
          <button data-testid="customer-auth-submit" disabled={busy}
            className="w-full px-4 py-3 bg-brand-red text-white font-bold uppercase tracking-wider text-sm hover:bg-[#B91C2B] disabled:opacity-60">
            {busy ? "One moment…" : mode === "login" ? "Log in" : "Create account"}
          </button>
        </form>

        <button data-testid="customer-auth-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="mt-5 text-sm text-zinc-400 hover:text-brand-red">
          {mode === "login" ? "New here? Create an account" : "Already have an account? Log in"}
        </button>
        <p className="mt-4 text-xs text-zinc-600">
          No account needed to book — you can always <Link to="/book" className="text-brand-red hover:underline">book as a guest</Link>.
        </p>
      </div>
    </div>
  );
}
